import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import VideoPlayerModal, { PortfolioVideo } from "@/components/VideoPlayerModal";
import { Play, Eye, Heart, MapPin, MessageCircle, TrendingUp, Clock, Filter, Sparkles, Rocket } from "lucide-react";
import { cn } from "@/lib/utils";
import { getWhatsAppLink } from "@/lib/whatsapp";

type VideoData = PortfolioVideo;

const formatNumber = (num: number) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const formatBadgeColor = (type: string | null) => {
  if (type === "short") return "from-neon-cyan to-neon-green";
  if (type === "medium") return "from-neon-purple to-neon-pink";
  if (type === "long") return "from-neon-orange to-neon-pink";
  return "from-neon-purple to-neon-cyan";
};

const formatLabel = (type: string | null) => {
  if (type === "short") return "13–15s";
  if (type === "medium") return "30s";
  if (type === "long") return "60s";
  return type || "";
};

type SortOption = "latest" | "views" | "revenue";

const VideoCard = ({ video, onClick }: { video: VideoData; onClick: () => void }) => {
  const whatsappMsg = `Hi! I saw your "${video.title}" video and I'd like something similar for my business 🚀`;

  return (
    <div className="group/card relative animate-fade-in">
      <button
        onClick={onClick}
        className="relative text-left w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-neon-cyan/50 rounded-2xl"
      >
        <div className="relative aspect-[9/16] rounded-2xl overflow-hidden border border-white/[0.08] hover:border-neon-cyan/30 active:border-neon-cyan/50 transition-all duration-300 hover:shadow-[0_0_40px_-10px_hsl(var(--neon-cyan)/0.2),inset_0_1px_0_0_rgba(255,255,255,0.05)] hover:scale-[1.02] active:scale-[0.98] bg-black/20">
          {video.thumbnail_url ? (
            <img src={video.thumbnail_url} alt={video.title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-neon-purple/30 via-neon-pink/20 to-neon-cyan/30" />
          )}
          {/* Stronger gradient for text legibility */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent via-50% to-black/90" />

          {/* Play overlay — always visible on mobile, hover on desktop */}
          <div className="absolute inset-0 flex items-center justify-center md:opacity-0 md:group-hover/card:opacity-100 opacity-100 transition-opacity duration-200 pointer-events-none">
            <div className="w-11 h-11 md:w-13 md:h-13 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center border border-white/15 shadow-[0_0_20px_hsl(var(--neon-purple)/0.25)]">
              <Play className="w-5 h-5 md:w-6 md:h-6 text-white ml-0.5" fill="currentColor" />
            </div>
          </div>

          {/* Format badge */}
          {video.format_type && (
            <div className={cn(
              "absolute top-2 left-2 md:top-3 md:left-3 z-10 px-2 py-0.5 rounded-full bg-gradient-to-r text-[8px] md:text-[9px] font-bold text-white uppercase tracking-wider flex items-center gap-0.5 md:gap-1 shadow-lg",
              formatBadgeColor(video.format_type)
            )}>
              <Clock className="w-2 h-2 md:w-2.5 md:h-2.5" />
              {formatLabel(video.format_type)}
            </div>
          )}

          {/* Industry badge */}
          <div className="absolute top-2 right-2 md:top-3 md:right-3 z-10 flex flex-col gap-1">
            <div className="flex items-center gap-1 bg-black/50 backdrop-blur-sm rounded-full px-1.5 md:px-2 py-0.5 border border-white/[0.06]">
              <Eye className="w-2 h-2 md:w-2.5 md:h-2.5 text-gray-300" />
              <span className="text-[8px] md:text-[9px] text-white font-bold tabular-nums">{formatNumber(video.views)}</span>
            </div>
            <div className="flex items-center gap-1 bg-black/50 backdrop-blur-sm rounded-full px-1.5 md:px-2 py-0.5 border border-white/[0.06]">
              <Heart className="w-2 h-2 md:w-2.5 md:h-2.5 text-pink-400" />
              <span className="text-[8px] md:text-[9px] text-white font-bold tabular-nums">{formatNumber(video.likes)}</span>
            </div>
          </div>

          {/* Bottom info */}
          <div className="absolute bottom-0 left-0 right-0 p-2.5 md:p-3 z-10">
            <p className="text-xs md:text-sm font-bold text-white truncate leading-tight">{video.title}</p>
            <div className="flex items-center gap-1 text-[9px] md:text-[10px] text-white/50 mt-0.5">
              <MapPin className="w-2 h-2 md:w-2.5 md:h-2.5 flex-shrink-0" />
              <span className="truncate">{video.location}</span>
              <span className="mx-0.5 text-white/20">·</span>
              <span className="truncate text-neon-cyan/60">{video.industry}</span>
            </div>
            {video.campaign_type && (
              <div className="text-[9px] md:text-[10px] text-neon-cyan/70 mt-0.5 truncate">{video.campaign_type}</div>
            )}
            {video.revenue > 0 && (
              <div className="flex items-center gap-1 bg-neon-green/10 border border-neon-green/20 rounded-md px-1.5 md:px-2 py-0.5 w-fit mt-1">
                <TrendingUp className="w-2 h-2 md:w-2.5 md:h-2.5 text-neon-green" />
                <span className="text-[9px] md:text-[10px] font-bold text-neon-green tabular-nums">+€{formatNumber(video.revenue)}</span>
              </div>
            )}
          </div>
        </div>
      </button>

      {/* WhatsApp micro-CTA — always visible on mobile, hover on desktop */}
      <a
        href={getWhatsAppLink(whatsappMsg)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="flex items-center justify-center gap-1.5 w-full mt-2 py-2 md:py-1.5 rounded-xl bg-gradient-to-r from-neon-green/90 to-neon-cyan/90 text-white text-[10px] md:text-[11px] font-bold md:opacity-0 md:translate-y-1 md:group-hover/card:opacity-100 md:group-hover/card:translate-y-0 transition-all duration-200 active:scale-95 shadow-[0_2px_12px_hsl(var(--neon-green)/0.15)]"
      >
        <MessageCircle className="w-3 h-3" />
        Get This Video
      </a>
    </div>
  );
};

const Portfolio = () => {
  const [videos, setVideos] = useState<VideoData[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState<VideoData | null>(null);
  const [industryFilter, setIndustryFilter] = useState("all");
  const [formatFilter, setFormatFilter] = useState("all");
  const [sort, setSort] = useState<SortOption>("latest");

  useEffect(() => {
    const fetchVideos = async () => {
      const { data, error } = await supabase
        .from("portfolio_videos")
        .select("*")
        .order("order_index", { ascending: true });
      if (!error && data) setVideos(data as VideoData[]);
      setLoading(false);
    };
    fetchVideos();
  }, []);

  const industries = ["all", ...Array.from(new Set(videos.map((v) => v.industry.trim())))];
  const formats = ["all", "short", "medium", "long"];

  let filtered = videos;
  if (industryFilter !== "all") filtered = filtered.filter((v) => v.industry.trim() === industryFilter);
  if (formatFilter !== "all") filtered = filtered.filter((v) => v.format_type === formatFilter);

  if (sort === "views") filtered = [...filtered].sort((a, b) => b.views - a.views);
  else if (sort === "revenue") filtered = [...filtered].sort((a, b) => b.revenue - a.revenue);

  const totalViews = videos.reduce((s, v) => s + v.views, 0);
  const totalRevenue = videos.reduce((s, v) => s + v.revenue, 0);

  return (
    <>
      <SEOHead />
      <Navbar />
      <main className="min-h-screen gradient-mesh pt-20 pb-16">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Hero header */}
          <div className="text-center mb-10 md:mb-14 pt-8 md:pt-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-neon-purple/10 border border-neon-purple/20 text-neon-purple text-xs font-medium mb-5 animate-fade-in">
              <Sparkles className="w-3 h-3" />
              {videos.length} Videos · {industries.length - 1} Industries
            </div>
            <h1 className="font-heading font-bold text-3xl md:text-5xl lg:text-6xl text-foreground mb-3 md:mb-4 animate-fade-in">
              Our{" "}
              <span className="bg-gradient-to-r from-neon-green via-neon-cyan to-neon-purple bg-clip-text text-transparent bg-[length:200%_auto] animate-[gradient-shift_4s_ease-in-out_infinite]">
                Portfolio
              </span>
            </h1>
            <p className="text-muted-foreground max-w-xl mx-auto text-sm md:text-base lg:text-lg animate-fade-in">
              Every video was made for a real business. Tap to play and see what we can create for yours.
            </p>

            {/* Aggregate stats — glass cards */}
            <div className="flex items-stretch justify-center gap-3 md:gap-4 mt-6 md:mt-8 animate-fade-in">
              {[
                { value: formatNumber(totalViews), label: "Total Views", color: "text-foreground" },
                { value: `€${formatNumber(totalRevenue)}`, label: "Revenue Generated", color: "text-neon-green" },
                { value: `${videos.length}`, label: "Videos Made", color: "text-foreground" },
              ].map((stat) => (
                <div key={stat.label} className="flex-1 max-w-[140px] md:max-w-[160px] bg-white/[0.03] backdrop-blur-sm border border-white/[0.06] rounded-xl px-3 py-3 md:px-4 md:py-4 hover:bg-white/[0.06] hover:border-white/[0.1] transition-all duration-300">
                  <div className={cn("text-lg md:text-2xl font-heading font-bold tabular-nums", stat.color)}>{stat.value}</div>
                  <div className="text-[9px] md:text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Sticky filter bar */}
          <div className="sticky top-16 z-30 -mx-4 px-4 py-3 bg-background/80 backdrop-blur-xl border-b border-white/[0.04] mb-6 md:mb-8">
            <div className="flex flex-col gap-3 p-3 md:p-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
              {/* Industry pills */}
              <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1 -mb-1">
                <div className="flex items-center gap-1.5 text-[10px] md:text-xs text-muted-foreground mr-1 flex-shrink-0">
                  <Filter className="w-3 h-3" /> Industry
                </div>
                {industries.map((ind) => (
                  <button
                    key={ind}
                    onClick={() => setIndustryFilter(ind)}
                    className={cn(
                      "px-3 md:px-3.5 py-2 md:py-2 rounded-full text-[10px] md:text-xs font-medium border transition-all duration-200 whitespace-nowrap flex-shrink-0 min-h-[36px] md:min-h-[40px] flex items-center",
                      industryFilter === ind
                        ? "bg-neon-cyan/15 border-neon-cyan/40 text-neon-cyan shadow-[0_0_15px_hsl(var(--neon-cyan)/0.2)]"
                        : "bg-white/[0.03] border-white/[0.08] text-muted-foreground hover:border-white/20 active:bg-white/[0.08]"
                    )}
                  >
                    {ind === "all" ? "All" : ind}
                  </button>
                ))}
              </div>

              {/* Format + Sort row */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1 border border-white/[0.08] rounded-full p-0.5 bg-white/[0.02]">
                  {formats.map((f) => (
                    <button
                      key={f}
                      onClick={() => setFormatFilter(f)}
                      className={cn(
                        "px-2.5 md:px-3 py-1.5 md:py-2 rounded-full text-[10px] md:text-xs font-medium transition-all min-h-[32px] md:min-h-[36px]",
                        formatFilter === f
                          ? "bg-white/10 text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground active:bg-white/5"
                      )}
                    >
                      {f === "all" ? "All" : formatLabel(f)}
                    </button>
                  ))}
                </div>

                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value as SortOption)}
                  className="bg-white/[0.04] border border-white/[0.08] rounded-lg px-2.5 md:px-3 py-2 text-[10px] md:text-xs text-foreground cursor-pointer outline-none focus:border-neon-cyan/40 min-h-[36px] transition-colors"
                >
                  <option value="latest">Latest</option>
                  <option value="views">Most Views</option>
                  <option value="revenue">Top Revenue</option>
                </select>
              </div>
            </div>
          </div>

          {/* Grid */}
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i}>
                  <div className="aspect-[9/16] rounded-2xl bg-white/[0.03] animate-pulse border border-white/[0.04]" />
                  <div className="mt-2 h-6 bg-white/[0.03] rounded-lg animate-pulse w-full" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 animate-fade-in">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-neon-purple/10 to-neon-cyan/10 flex items-center justify-center border border-white/[0.06]">
                <Rocket className="w-8 h-8 text-muted-foreground/40" />
              </div>
              <p className="text-muted-foreground text-base mb-1">No videos match your filters</p>
              <p className="text-muted-foreground/60 text-sm mb-4">Try widening your search criteria</p>
              <button
                onClick={() => { setIndustryFilter("all"); setFormatFilter("all"); }}
                className="inline-flex items-center gap-1.5 text-neon-cyan text-sm font-medium hover:text-neon-green transition-colors"
              >
                <Filter className="w-3.5 h-3.5" />
                Clear all filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {filtered.map((video) => (
                <VideoCard key={video.id} video={video} onClick={() => setActiveVideo(video)} />
              ))}
            </div>
          )}

          {/* Bottom CTA */}
          <div className="text-center mt-12 md:mt-16 animate-fade-in">
            <a
              href={getWhatsAppLink("Hi! I saw your portfolio and I'd like a free sample video for my business 🚀")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 px-7 md:px-8 py-3.5 md:py-4 rounded-full bg-gradient-to-r from-neon-green to-neon-cyan font-heading font-bold text-sm md:text-base text-background hover:scale-105 active:scale-95 transition-all duration-200 shadow-[0_0_30px_hsl(var(--neon-green)/0.3)] hover:shadow-[0_0_50px_hsl(var(--neon-green)/0.4)] animate-[pulse-glow_3s_ease-in-out_infinite]"
            >
              <MessageCircle className="w-4 h-4 md:w-5 md:h-5" />
              Want one for your business? Get a free sample →
            </a>
          </div>
        </div>
      </main>

      {activeVideo && (
        <VideoPlayerModal
          video={activeVideo}
          videos={filtered}
          onClose={() => setActiveVideo(null)}
          onNavigate={setActiveVideo}
        />
      )}

      <Footer />
    </>
  );
};

export default Portfolio;
